import React, {useCallback, useEffect, useState} from 'react';
import request from '../../util/SuperagentUtils';
import {deleteInventoryCorrection} from '../../components/inventory-correction/requests/inventory-correction-requests';
import {
  getErrorMessageForNonStandardAndStandardResponse,
  getErrorMessageForStandardResponse
} from "../../util/NetworkErrorUtil";
import InventoryRecordsTable from "../../components/daily-activity/InventoryRecordsTable";
import {Button, Grid} from "@mui/material";
import {PaymentHeading} from "../booking-payment/style";
import {ArrowBack} from '@mui/icons-material';
import {formatSpaceUsageItem} from "../../components/spaceUsage/formatSpaceUsage";
import {withSnackbar} from '../../components/hocs/withSnackbar';
import SearchEquipmentType from "./SearchEquipmentType";
import {getComparator, stableSort} from '../../util/Sorting';
import {buyerInventoryCorrectionReport} from '../../requests/api-endpoints';
import moment from "moment";
import {DateFormats} from "../../components/constants/securspace-constants";
import NoDataMessage from "../../components/deprecatedUikit/NoDataMessage/NoDataMessage";
import SpaceUsageCard from "../../components/deprecatedUikit/SpaceUsageCard/SpaceUsageCard";
import Pagination from "../../components/deprecatedUikit/Pagination/Pagination";

const InventoryRecords = (props) => {
  const {inventoryDate, spaceUtilizationRecord, closeInventoryRecords, snackbarShowMessage } = props;
  const spaceUsageItem = formatSpaceUsageItem(spaceUtilizationRecord);

  const [isLoading, setIsLoading] = useState(false);
  const [inventoryRecords, setInventoryRecords] = useState([]);
  const [filteredInventoryRecords, setFilteredInventoryRecords] = useState([]);
  const [equipmentNumber, setEquipmentNumber] = useState('');
  const [sort, setSort] = useState('');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const emptyRows = filteredInventoryRecords?.length;


  const loadInventory = useCallback(() => {
    setIsLoading(true);
    request.get(buyerInventoryCorrectionReport.search).query({
      inventoryDate,
      locationId: spaceUtilizationRecord?.locationId
    })
    .then((response) => {
      setInventoryRecords(response.body);
    })
    .catch((error) => {
      console.error(getErrorMessageForNonStandardAndStandardResponse(error));
    })
    .finally(() => {
      setIsLoading(false);
    });
  }, [inventoryDate, spaceUtilizationRecord]);

  useEffect(() => {
    let ignore = false;
    if (!ignore) {
      loadInventory();
    }
    return () => {
      ignore = true;
    };
  }, [loadInventory]);

  useEffect(() => {
    const filtered = inventoryRecords.filter((record) => {
      if (!equipmentNumber) {
        return true;
      }
      let search = equipmentNumber.trim().toLowerCase();
      return record.containerNumber?.toLowerCase().includes(search) ||
        record.chassisNumber?.toLowerCase().includes(search) ||
        record.trailerNumber?.toLowerCase().includes(search) ||
        record.truckLicensePlateNumber?.toLowerCase().includes(search);
    });
    setFilteredInventoryRecords(filtered);
  }, [equipmentNumber, inventoryRecords]);

  const deleteCorrectionRequest = (correctionId) => {
    deleteInventoryCorrection(correctionId)
      .then(() => {
        loadInventory()
        snackbarShowMessage("Successfully retracted Inventory Correction.", "success");
      })
      .catch(err => snackbarShowMessage(getErrorMessageForStandardResponse(err), "error"))
  }

  const handlePageChange = (_, selectedPage) => {
    setPage(selectedPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEquipmentNumberChange = useCallback((value) => {
    setEquipmentNumber(value);
  }, []);

  const handleSortChange = (sort, order) => {
    setSort(sort);
    setOrder(order);
  };

  const visibleRows = React.useMemo(
    () =>
      stableSort(filteredInventoryRecords, getComparator(order, sort)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [sort, page, rowsPerPage, filteredInventoryRecords, order],
  );

  const handleSubmitInventoryCorrection = (inventory) => {
    const startDate = moment(inventory.checkinDate).subtract(1, 'day').format(DateFormats.DAY);
    const equipmentNumber = inventory.containerNumber ||
    inventory.truckLicensePlateNumber ||
    inventory.chassisLicensePlateNumber ||
    inventory.chassisNumber ||
    inventory.trailerNumber ||
    '';
    window.open(`/buyer-inventory-corrections?tab=REVIEW&equipmentNumber=${equipmentNumber}&startDate=${startDate}`, '_blank', 'noopener,noreferrer');
  }

  return <Grid container>
    <Grid container gap={2} sx={{ mb: 4, mt: 2, width: '100%' }} justifyContent='space-between'>
      <Grid xs={12} md={6} sx={{
        display: 'flex',
        alignItems: 'center',
      }} item>
        <Button sx={{
          minWidth: '0em',
          padding: '0em',
          mr: 0.4
        }} variant='text' onClick={closeInventoryRecords} >
          <ArrowBack />
        </Button>
        <PaymentHeading color={'textPrimary'} component={'h1'}>Daily Activity</PaymentHeading>
      </Grid>
      <Grid item xs={12} md={5}>
        <SearchEquipmentType value={equipmentNumber} onChange={handleEquipmentNumberChange} />
      </Grid>
    </Grid>
    <SpaceUsageCard
      data={spaceUsageItem}
      styles={{ mb: 4, backgroundColor: (theme) => theme.palette.common.white, width: '100%' }}
      elementStyleActive={true}
      variant='secondary'
    />
   {
      emptyRows === 0 && !isLoading ? <Grid item style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: 'auto',
      }}>
        <NoDataMessage
          image="/app-images/person-and-box.svg"
          message="No matching records found."
        />
      </Grid> :
        <>
          <InventoryRecordsTable
            isLoading={isLoading}
            inventoryRecords={visibleRows}
            setInventory={handleSubmitInventoryCorrection}
            deleteCorrection={deleteCorrectionRequest}
            handleSortChange={handleSortChange}
          />
          <Pagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            count={filteredInventoryRecords?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
    }
  </Grid>
}

export default withSnackbar(InventoryRecords);
